import noop from 'lodash/noop';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClaimsFileUpload } from '@hooks/react-query/mutations/useClaimsFileUpload';
import { useAuth } from '@context/AuthContext';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { ImportSettings } from './components/ImportsSettingsModal/ImportsSettingsModal.types';
import { buildFieldStatusSections } from './components/MatchingFieldsStatusDrawer/utils';
import { COLUMN_NUMBER_MIN } from './constants';
import {
  ChipIconType,
  ColumnKey,
  FileMatches,
  FileUploadView,
  NormalizeFile,
  RecalculateResponse,
} from './types';
import {
  buildMatchedHeaders,
  createFileWithPaddedZipCodes,
  createFileWithRemovedColumns,
  detectHeaders,
  findColumnIndexToNavigate,
  getUnmatchedColumnIndexes,
  isFileMatchError,
  matchedFields,
  normalizeCSVData,
  parseCsvPreview,
} from './utils';

interface UploadClaimsFileContextProps {
  readonly formattedFile: NormalizeFile | null;
  readonly setFormattedFile: (file: NormalizeFile | null) => void;
  readonly delimiter: string;
  readonly setDelimiter: (delimiter: string) => void;
  readonly escapeChar: string;
  readonly setEscapeChar: (escapeChar: string) => void;
  readonly header: boolean;
  readonly setHeader: (header: boolean) => void;
  readonly fileMatches: FileMatches;
  readonly setFileMatches: (fileMatches: FileMatches) => void;
  readonly fileUploadView: FileUploadView;
  readonly setFileUploadView: (view: FileUploadView) => void;
  readonly helpDrawerOpen: boolean;
  readonly setHelpDrawerOpen: (open: boolean) => void;
  readonly fieldsStatusDrawerOpen: boolean;
  readonly setFieldsStatusDrawerOpen: (open: boolean) => void;
  readonly importSettingsModalOpen: boolean;
  readonly setImportSettingsModalOpen: (open: boolean) => void;
  readonly selectedFilterChip: ChipIconType;
  readonly setSelectedFilterChip: (chip: ChipIconType) => void;
  readonly filteredMatches: FileMatches;
  readonly activeColumnIndex: number;
  readonly setActiveColumnIndex: (index: number) => void;
  readonly goToColumn: (matchKeys: readonly string[]) => void;
  readonly rawFile: File | null;
  readonly setRawFile: (file: File | null) => void;
  readonly recalculateFile: (
    settings: ImportSettings,
  ) => Promise<RecalculateResponse>;
  readonly handleFileUpload: () => Promise<void>;
  readonly isFileLoading: boolean;
  readonly uploadProgress: number;
  readonly uploadHasError: boolean;
  readonly setUploadHasError: (v: boolean) => void;
  readonly isAllRequiredFieldsMatched: boolean;
  readonly isAllRecommendedFieldsMatched: boolean;
  readonly requiredFields: number;
  readonly requiredMatchedFields: number;
  readonly recommendedFields: number;
  readonly recommendedMatchedFields: number;
  readonly optionalFields: number;
  readonly optionalMatchedFields: number;
}

export const UploadClaimsFileContext =
  createContext<UploadClaimsFileContextProps>({
    formattedFile: null,
    setFormattedFile: noop,
    delimiter: ',',
    setDelimiter: noop,
    escapeChar: '"',
    setEscapeChar: noop,
    header: false,
    setHeader: noop,
    fileMatches: [],
    setFileMatches: noop,
    fileUploadView: FileUploadView.UPLOAD,
    setFileUploadView: noop,
    helpDrawerOpen: false,
    setHelpDrawerOpen: noop,
    fieldsStatusDrawerOpen: false,
    setFieldsStatusDrawerOpen: noop,
    importSettingsModalOpen: false,
    setImportSettingsModalOpen: noop,
    rawFile: null,
    setRawFile: noop,
    recalculateFile: () => Promise.resolve({} as RecalculateResponse),
    selectedFilterChip: ChipIconType.ALL,
    setSelectedFilterChip: noop,
    filteredMatches: [],
    activeColumnIndex: 0,
    setActiveColumnIndex: noop,
    goToColumn: noop,
    handleFileUpload: () => Promise.resolve(),
    isFileLoading: false,
    uploadProgress: 0,
    uploadHasError: false,
    setUploadHasError: noop,
    isAllRequiredFieldsMatched: false,
    isAllRecommendedFieldsMatched: false,
    requiredFields: 0,
    requiredMatchedFields: 0,
    recommendedFields: 0,
    recommendedMatchedFields: 0,
    optionalFields: 0,
    optionalMatchedFields: 0,
  });

export const SETTINGS_DEFAULT_VALUES = {
  DELIMITER: ',',
  ESCAPE_CHAR: '"',
  HEADER: false,
};

export function UploadClaimsFileProvider({ children }: PropsWithChildren) {
  const { selectedCase } = useUserOrgCase();
  const { user } = useAuth();
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [formattedFile, setFormattedFile] = useState<NormalizeFile | null>(
    null,
  );
  const [delimiter, setDelimiter] = useState<string>(
    SETTINGS_DEFAULT_VALUES.DELIMITER,
  );
  const [escapeChar, setEscapeChar] = useState<string>(
    SETTINGS_DEFAULT_VALUES.ESCAPE_CHAR,
  );
  const [header, setHeader] = useState<boolean>(SETTINGS_DEFAULT_VALUES.HEADER);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [fieldsStatusDrawerOpen, setFieldsStatusDrawerOpen] = useState(false);
  const [importSettingsModalOpen, setImportSettingsModalOpen] = useState(false);
  const [fileUploadView, setFileUploadView] = useState<FileUploadView>(
    FileUploadView.UPLOAD,
  );
  const [fileMatches, setFileMatches] = useState<FileMatches>([]);
  const [selectedFilterChip, setSelectedFilterChip] = useState<ChipIconType>(
    ChipIconType.ALL,
  );
  const [activeColumnIndex, setActiveColumnIndex] = useState(0);

  useEffect(() => {
    if (fileUploadView !== FileUploadView.MATCH_COLUMNS) {
      setFieldsStatusDrawerOpen(false);
    }
  }, [fileUploadView, setFieldsStatusDrawerOpen]);

  const { mutateAsync: submitBulkUploadFile, isPending } =
    useClaimsFileUpload();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadHasError, setUploadHasError] = useState(false);

  const fieldStatusSections = useMemo(
    () => buildFieldStatusSections(fileMatches),
    [fileMatches],
  );

  const requiredFields = fieldStatusSections.required.length;
  const recommendedFields = fieldStatusSections.recommended.length;
  const optionalFields = fieldStatusSections.optional.length;

  const requiredMatchedFields = useMemo(
    () =>
      fieldStatusSections.required.filter((entry) => entry.status !== 'pending')
        .length,
    [fieldStatusSections],
  );

  const recommendedMatchedFields = useMemo(
    () =>
      fieldStatusSections.recommended.filter(
        (entry) => entry.status !== 'pending',
      ).length,
    [fieldStatusSections],
  );

  const optionalMatchedFields = useMemo(
    () =>
      fieldStatusSections.optional.filter((entry) => entry.status !== 'pending')
        .length,
    [fieldStatusSections],
  );

  const isAllRequiredFieldsMatched = useMemo(
    () => requiredFields > 0 && requiredMatchedFields === requiredFields,
    [requiredMatchedFields, requiredFields],
  );

  const isAllRecommendedFieldsMatched = useMemo(
    () => recommendedMatchedFields === recommendedFields,
    [recommendedMatchedFields, recommendedFields],
  );

  const filteredMatches = useMemo(() => {
    switch (selectedFilterChip) {
      case ChipIconType.MATCHED:
        return fileMatches.filter((f) => f.matched);
      case ChipIconType.UNMATCHED:
        return fileMatches.filter((f) => !f.matched);
      case ChipIconType.ERROR:
        return fileMatches.filter(isFileMatchError);
      case ChipIconType.ALL:
      default:
        return fileMatches;
    }
  }, [selectedFilterChip, fileMatches]);

  const goToColumn = useCallback(
    (matchKeys: readonly string[]) => {
      const index = findColumnIndexToNavigate(fileMatches, matchKeys);
      if (index === null) return;

      setSelectedFilterChip(ChipIconType.ALL);
      setActiveColumnIndex(index);
    },
    [fileMatches, setSelectedFilterChip],
  );

  useEffect(() => {
    setFileMatches(matchedFields(formattedFile));
  }, [formattedFile, setFileMatches, fileMatches.length]);

  const recalculateFile: (
    settings: ImportSettings,
  ) => Promise<RecalculateResponse> = useCallback(
    (settings: ImportSettings) => {
      const errorResult = {
        recalculated: false,
        error:
          'Looks like the settings you have selected do not match the file you are trying to upload. Please, try changing the settings.',
      };

      if (!rawFile) return Promise.reject(errorResult);

      return new Promise((resolve, reject) => {
        parseCsvPreview(
          rawFile,
          {
            header: settings.header,
            delimiter: settings.delimiter,
            escapeChar: settings.escapeChar,
          },
          {
            complete: (results) => {
              if (results.data.length === 0) {
                reject(errorResult);
                return;
              }

              const normalizedFile = normalizeCSVData(results);
              normalizedFile.filename = rawFile.name;

              if (
                normalizedFile.headers &&
                normalizedFile.headers.length < COLUMN_NUMBER_MIN
              ) {
                reject(errorResult);
                return;
              }

              setFormattedFile(normalizedFile);
              resolve({ recalculated: true, error: null });
            },
            error: () => {
              reject(errorResult);
            },
          },
        );
      });
    },
    [rawFile],
  );

  const handleFileUpload = useCallback(async () => {
    if (!rawFile || !formattedFile) {
      throw new Error('No file or formatted data available for upload.');
    }

    const matchedHeaders = buildMatchedHeaders(fileMatches);

    const fileContainerHeader = detectHeaders(formattedFile.headers);

    // Find zip code column indices for padding
    const zipColumnIndices: number[] = [];
    fileMatches.forEach((match) => {
      if (match.field === ColumnKey.Zip && match.columnIndex !== null) {
        zipColumnIndices.push(match.columnIndex);
      }
    });

    // Transform file with padded zip codes if needed
    const zipPaddedFile = await createFileWithPaddedZipCodes({
      file: rawFile,
      zipColumnIndices,
    });

    const fileToUpload = await createFileWithRemovedColumns({
      file: zipPaddedFile,
      columnIndicesToRemove: getUnmatchedColumnIndexes(fileMatches),
    });

    await submitBulkUploadFile({
      file: fileToUpload,
      caseID: selectedCase.caseID,
      uploaderEmail: user?.email || '',
      userId: user?.uid || '',
      containsHeader: fileContainerHeader,
      headers: matchedHeaders.toString(),
      delimiter,
      escapeChar,
      onProgress: setUploadProgress,
    });
  }, [
    delimiter,
    escapeChar,
    fileMatches,
    formattedFile,
    rawFile,
    selectedCase.caseID,
    submitBulkUploadFile,
    user?.email,
    user?.uid,
  ]);

  const contextValue = useMemo(
    () => ({
      formattedFile,
      setFormattedFile,
      delimiter,
      setDelimiter,
      escapeChar,
      setEscapeChar,
      header,
      setHeader,
      fileUploadView,
      setFileUploadView,
      fileMatches,
      setFileMatches,
      helpDrawerOpen,
      setHelpDrawerOpen,
      fieldsStatusDrawerOpen,
      setFieldsStatusDrawerOpen,
      importSettingsModalOpen,
      setImportSettingsModalOpen,
      rawFile,
      setRawFile,
      recalculateFile,
      selectedFilterChip,
      setSelectedFilterChip,
      filteredMatches,
      activeColumnIndex,
      setActiveColumnIndex,
      goToColumn,
      handleFileUpload,
      isFileLoading: isPending,
      uploadProgress,
      uploadHasError,
      setUploadHasError,
      isAllRequiredFieldsMatched,
      isAllRecommendedFieldsMatched,
      requiredFields,
      requiredMatchedFields,
      recommendedFields,
      recommendedMatchedFields,
      optionalFields,
      optionalMatchedFields,
    }),
    [
      formattedFile,
      delimiter,
      escapeChar,
      header,
      fileUploadView,
      fileMatches,
      helpDrawerOpen,
      fieldsStatusDrawerOpen,
      importSettingsModalOpen,
      rawFile,
      recalculateFile,
      selectedFilterChip,
      filteredMatches,
      activeColumnIndex,
      goToColumn,
      handleFileUpload,
      isPending,
      uploadProgress,
      uploadHasError,
      isAllRequiredFieldsMatched,
      isAllRecommendedFieldsMatched,
      requiredFields,
      requiredMatchedFields,
      recommendedFields,
      recommendedMatchedFields,
      optionalFields,
      optionalMatchedFields,
    ],
  );

  return (
    <UploadClaimsFileContext.Provider value={contextValue}>
      {children}
    </UploadClaimsFileContext.Provider>
  );
}

export const useUploadClaimsFileContext = () =>
  useContext(UploadClaimsFileContext);
